/**
 * Hanko (印鑑 / 朱印) e-stamp. Renders the approver's name inside a red
 * circular seal — the JP-business universal "I approved this" marker.
 *
 * The name is rendered vertically (top-to-bottom) inside the circle,
 * matching how a physical 三文判 reads. Up to 4 kanji fit cleanly;
 * longer names truncate to the first 4 characters (the convention).
 *
 * Two states:
 *   - `state="stamped"` — red ink, drop shadow, slight rotation for the
 *     hand-stamped feel.
 *   - `state="empty"` — dashed outline, gray text "未押印 (Not stamped)"
 *     so the approval flow can show the slot before the approver acts.
 *
 * Usage:
 *   <HankoStamp name="山田" state="stamped" />
 *   <HankoStamp name="山田太郎" state="empty" />
 */
export function HankoStamp({
  name,
  state = 'stamped',
  size = 88,
  date,
}: {
  name: string;
  state?: 'stamped' | 'empty';
  size?: number;
  date?: string | Date | null;
}) {
  // Take the first 4 kanji-ish characters. Romaji names look poor in a
  // vertical hanko, so we cap to 4 chars regardless of script.
  const chars = Array.from(name).filter((c) => c.trim()).slice(0, 4);

  if (state === 'empty') {
    return (
      <div
        className="inline-flex flex-col items-center justify-center rounded-full border-[1.5px] border-dashed border-ink-300 bg-ink-50/60 text-ink-400"
        style={{ width: size, height: size, fontSize: size * 0.13 }}
        aria-label="Awaiting hanko"
      >
        <span className="font-semibold tracking-widest">未押印</span>
      </div>
    );
  }

  const rotation = ((name.charCodeAt(0) || 0) % 7) - 3; // deterministic small tilt
  return (
    <div className="inline-flex flex-col items-center gap-1.5">
      <div
        className="relative inline-flex items-center justify-center rounded-full border-[2.5px] border-citation-500 text-citation-500 select-none"
        style={{
          width: size,
          height: size,
          background: 'radial-gradient(circle at 38% 32%, rgba(249,115,22,0.08), rgba(249,115,22,0.02) 70%)',
          transform: `rotate(${rotation}deg)`,
          boxShadow: '0 1px 0 rgba(249,115,22,0.18), 0 2px 6px rgba(249,115,22,0.08)',
          fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
        }}
        aria-label={`Hanko: ${name}`}
        title={name}
      >
        <div className="flex flex-col items-center leading-[0.95] font-semibold" style={{ fontSize: chars.length <= 2 ? size * 0.34 : size * 0.26 }}>
          {chars.map((c, i) => <span key={i}>{c}</span>)}
        </div>
      </div>
      {date && (
        <span className="text-[10.5px] num text-ink-500 tracking-wider">
          {typeof date === 'string' ? date : date.toLocaleDateString('ja-JP')}
        </span>
      )}
    </div>
  );
}
